// Location org tool links editor (merged with TOOL_LINKS + custom types).
//
// Slice 14: the third mode ("Manage locations") was upgraded from a
// primitive single-field add-location form to the full
// OrgLocationsSection (add / edit / paginate / search-by-row). Trainers
// + marketing can add and edit; only admin/owner can deactivate (the
// section hides the toggle when `allowDeactivate={false}`, and the
// backend enforces the same boundary).
import { OrgLocationsSection } from "@/components/admin/OrgLocationsSection";
import { LocationToolsAddTypeModal } from "./LocationToolsAddTypeModal";
import { LocationToolsByLocationSection } from "./LocationToolsByLocationSection";
import { LocationToolsContentToolbar } from "./LocationToolsContentToolbar";
import { LocationToolsLinkTypesSection } from "./LocationToolsLinkTypesSection";
import { useLocationToolsContentModel } from "./useLocationToolsContentModel";
import { useAuth } from "@/contexts/AuthContext";

export type { LocationToolsMode } from "./locationToolsContentConstants";

type Props = {
  orgId: string | null;
  locations: { id: string; name: string }[];
  onLocationsUpdated?: () => void;
};

export function LocationToolsContent(props: Props) {
  const vm = useLocationToolsContentModel(props);
  const auth = useAuth();
  // Slice 14: only admin/owner/platform_* get the deactivate affordance.
  // Trainers see add+edit, no soft-delete button.
  const canDeactivateLocations =
    auth.status === "ready" && auth.hasRole("admin", "owner", "platform_owner", "platform_admin", "platform_dev");

  if (!vm.orgId) {
    return (
      <p className="text-sm text-amber-800 dark:text-amber-200/90">
        Select a workspace organization (header) to edit links for your org.
      </p>
    );
  }

  if (vm.loadError) {
    return (
      <p className="text-sm text-red-700 dark:text-red-300">
        {vm.loadError}
        <button type="button" className="ml-2 underline" onClick={() => void vm.load()}>
          Retry
        </button>
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <LocationToolsContentToolbar vm={vm} />

      <p className="text-sm text-gray-600 dark:text-gray-400">
        These URLs power the <strong>Location Links</strong> panel on the Leads screen. Org defaults apply everywhere;
        per-location values override when set.
      </p>

      {!vm.canEdit ? (
        <p className="text-sm text-amber-800 dark:text-amber-200/90 rounded-lg border border-amber-200 dark:border-amber-800 px-3 py-2">
          Your role can view these links but not edit. Ask a trainer, marketing, admin, or owner to update them.
        </p>
      ) : null}

      {vm.loading ? <p className="text-sm text-gray-500">Loading saved links…</p> : null}

      {vm.mode === "linkTypes" ? <LocationToolsLinkTypesSection vm={vm} /> : null}
      {vm.mode === "byLocation" ? <LocationToolsByLocationSection vm={vm} /> : null}
      {vm.mode === "addLocation" ? (
        <OrgLocationsSection
          organizationId={vm.orgId}
          plain
          allowDeactivate={canDeactivateLocations}
          onLocationsChanged={props.onLocationsUpdated}
        />
      ) : null}

      <LocationToolsAddTypeModal vm={vm} />
    </div>
  );
}
